import { useMemo } from 'react';
import useTenantBulletDefaults from './useTenantBulletDefaults';
import { appConfig } from '@config';
import { useMediaProviders, useFeatures } from '@hooks';
import MediaUpload from '@components/mediaUpload';
import { AssetTypes } from './studioContext/CMSParams';
import getStorySaveMarkup from '@src/web-stories-wp/story-editor/src/output/utils/getStorySaveMarkup';
import {
  ANIMATED_THUMBNAIL_ASPECT_RATIO,
  ANIMATED_THUMBNAIL_DURATION_SECONDS,
} from '@components/animatedThumbnailEditor/animatedThumbnailContract';

const { flags } = appConfig;

const mapFeatureFlags = (features) => {
  let studioFeatures = {};
  for (const [key, { value }] of Object.entries(features)) {
    studioFeatures[key] = value;
  }

  return studioFeatures;
};

const createConfig = ({
  assetType,
  animatedThumbnailConfig,
  tenantBulletDefaults,
  mediaProviders,
  apiProvider,
  features,
}) => {
  const config = {
    ...appConfig.config,
    apiCallbacks: apiProvider,
    ...(assetType === AssetTypes.Story
      ? { getSaveContent: getStorySaveMarkup }
      : {}),
    tenantBulletDefaults,
    MediaUpload,
    mediaProviders: [],
    features: mapFeatureFlags(features),
    ...(animatedThumbnailConfig
      ? {
          animatedThumbnail: animatedThumbnailConfig,
        }
      : {}),
  };

  if (mediaProviders && mediaProviders.length > 0) {
    config.mediaProviders = mediaProviders.filter((provider) => provider.code);
  }

  return config;
};

const useStoryEditorConfig = ({
  animatedThumbnail,
  apiProvider,
  assetType,
}) => {
  const { mediaProviders } = useMediaProviders();
  const { features } = useFeatures();
  const { defaults: tenantBulletDefaults, isReady: bulletDefaultsReady } =
    useTenantBulletDefaults();
  const {
    aspectRatio,
    saveDisabledReason,
    selectStartSeconds,
    setVideoDuration,
    source,
    startSeconds,
  } = animatedThumbnail || {};
  const hasAnimatedThumbnail = Boolean(animatedThumbnail);

  const animatedThumbnailConfig = useMemo(
    () =>
      hasAnimatedThumbnail
        ? {
            aspectRatio: aspectRatio || ANIMATED_THUMBNAIL_ASPECT_RATIO,
            fixedDurationSeconds: ANIMATED_THUMBNAIL_DURATION_SECONDS,
            initialStartSeconds: startSeconds,
            playbackStartSeconds: startSeconds,
            onDurationChange: setVideoDuration,
            onTrim: selectStartSeconds,
            saveDisabledReason,
            sourceUrl: source?.url,
          }
        : null,
    [
      aspectRatio,
      hasAnimatedThumbnail,
      saveDisabledReason,
      selectStartSeconds,
      setVideoDuration,
      source?.url,
      startSeconds,
    ]
  );

  const config = useMemo(() => {
    if (!mediaProviders || !apiProvider || !features || !bulletDefaultsReady)
      return null;
    return createConfig({
      assetType,
      animatedThumbnailConfig,
      tenantBulletDefaults,
      mediaProviders,
      apiProvider,
      features,
    });
  }, [
    assetType,
    animatedThumbnailConfig,
    tenantBulletDefaults,
    bulletDefaultsReady,
    mediaProviders,
    features,
    apiProvider,
  ]);

  if (!config) return null;

  return { editorConfig: { flags, config }, mediaProviders };
};

export default useStoryEditorConfig;
